import { useDispatch, useSelector } from 'react-redux';
import isArray from 'lodash/isArray';
import type { MessageTranslator } from '@oneflowab/pomes';
import { useEffect, useRef, useState } from 'react';

import { getLocationSelector } from '@app/reducers/router';
import { FolderQueryProvider } from '@app/contexts/folder-query-context';
import * as agreementConstants from '@flow/libs/agreement/constants';
import adminPage from '@app/hocs/admin-page';
import {
  defaultSortWithScore,
  Filter,
  SearchWithQueryString,
  sortSanitizer,
} from '@app/hocs/search';
import {
  lifecycleFilterSanitizer,
  tagsFilterSanitizer,
  freeTextSanitizer,
} from '@flow/libs/agreement/sanitizers';
import { getRestoreToastIds, removeIdInRestoreToastList } from '@app/reducers/app';

import { ControlledSearch } from '@app/components/search';
import AgreementSort from '@app/components/agreement-sort';
import CheckboxGroup from '@app/components/checkbox-group';
import ColleagueSelector from '@app/components/colleague-selector';
import DateFilter from '@app/components/date-filter';
import TagSelector from '@app/components/tag-selector';
import WorkspaceSelector from '@app/components/workspace-selector';
import getColleaguesFilterProps from '@flow/libs/agreement/colleagues-filter-props';
import getWorkspacesFilterProps from '@flow/libs/agreement/workspaces-filter-props';
import { stateFilterOptions } from '@app/components/filter-options';

import toast from '@app/components/toasts';
import { TrashedDocumentListWithSearchResult } from './trashed-document-list/trashed-documents-list-with-search-result';

const filterableStates = [
  agreementConstants.STATE_DRAFT,
  agreementConstants.STATE_PENDING,
  agreementConstants.STATE_OVERDUE,
  agreementConstants.STATE_SIGNED,
  agreementConstants.STATE_DECLINED,
];

const minFreeTextSearchLength = 2;

type Params = {
  tags: string[];
  noTag?: null;
};

type MapperProps = {
  props: {
    [key: string]: any;
  };
};

type Props = {
  message: MessageTranslator;
  workspace: Oneflow.Workspace;
  path: string;
  currentUser: Oneflow.Position;
  dateFormat: string;
  withoutWorkspaceId: boolean;
  showWorkspaceName: boolean;
  hideImport: boolean;
  shouldShowWorkspacesFilter: boolean;
  sendWorkspaceIds: boolean;
  setWorkspacesOptions: (options: []) => void;
  workspacesOptions: [];
};

const renderWorkspacesFilter = ({
  message,
  currentUser,
  shouldShowWorkspacesFilter,
  setWorkspacesOptions,
  workspacesOptions,
}: Props) => {
  if (!shouldShowWorkspacesFilter) {
    return null;
  }

  return (
    <Filter
      data-testid="workspaces-filter"
      param="collectionIds"
      title={message({
        id: 'Workspaces',
        comment: "Section title. Search filter category title, as in 'search by workspaces'.",
      })}
      options={workspacesOptions}
      optionsCount={workspacesOptions.length}
      setOptions={setWorkspacesOptions}
      defaultValue={[]}
      component={WorkspaceSelector}
      {...getWorkspacesFilterProps()}
      positionId={currentUser?.id}
    />
  );
};

export const tagsOnValueChange = (tags: string[], updateParams: (params: Params) => void) => {
  let params: Params = {
    tags,
  };

  if (isArray(tags) && tags.length) {
    params = {
      ...params,
      noTag: null,
    };
  }

  updateParams(params);
};

export const TrashDocuments = ({
  currentUser,
  dateFormat,
  message,
  shouldShowWorkspacesFilter,
  workspace,
  sendWorkspaceIds,
  withoutWorkspaceId,
  showWorkspaceName,
}: Props) => {
  const dispatch = useDispatch();
  const [colleaguesOptions, setColleaguesOptions] = useState([]);
  const [tagsOptions, setTagsOptions] = useState([]);
  const [workspacesOptions, setWorkspacesOptions] = useState([]);
  const location = useSelector(getLocationSelector);
  const toastIds = useSelector(getRestoreToastIds);

  const searchParams = location.search.split('&');
  searchParams[0] = searchParams[0].substring(1);

  const getPlaceHolder = () => {
    if (!workspace || workspace?.virtual) {
      return message({
        id: 'Search documents',
        comment: 'Placeholder text used in search field when trying to find documents.',
      }) as string;
    }

    return message({
      id: 'Search in {workspace}',
      values: {
        workspace: workspace.name,
      },
      comment: 'Placeholder text used in search field when trying to find documents.',
    }) as string;
  };

  const toastIdsRef = useRef(toastIds);
  toastIdsRef.current = toastIds;

  // biome-ignore lint/correctness/useExhaustiveDependencies: The effect needs to be memoized
  useEffect(
    () => () => {
      toastIdsRef.current.forEach((toastId) => {
        toast.remove({
          toastId,
        });
        dispatch(removeIdInRestoreToastList(toastId));
      });
    },
    [],
  );

  return (
    <FolderQueryProvider>
      <SearchWithQueryString
        data-testid="search-with-query-string"
        queryName="search/contracts/all"
        renderTrashedResult
        keyword={
          <Filter
            clearFiltersOnSearch
            component={ControlledSearch}
            title={message({
              id: 'Keyword',
              comment: 'Full text filter name',
            })}
            onSanitize={(value: any) => freeTextSanitizer(value, minFreeTextSearchLength)}
            placeholder={getPlaceHolder()}
            param="q"
            defaultValue=""
            wrap={false}
            minSearchLength={minFreeTextSearchLength}
            autoFocus
          />
        }
        filters={
          <>
            {renderWorkspacesFilter({
              message,
              currentUser,
              shouldShowWorkspacesFilter,
              setWorkspacesOptions,
              workspacesOptions,
            })}
            <Filter
              param="positions"
              title={message({
                id: 'Colleagues',
                comment:
                  "Section title. Search filter category title, as in 'search by colleagues'.",
              })}
              defaultValue={[]}
              component={ColleagueSelector}
              options={colleaguesOptions}
              optionsCount={colleaguesOptions.length}
              setOptions={setColleaguesOptions}
              workspace={workspace}
              {...getColleaguesFilterProps(workspace, currentUser)}
            />
            <Filter
              param="removed"
              defaultValue={1}
            />
            <Filter
              param="state"
              title={message({
                id: 'Status',
                comment:
                  "Section title. Search filter category title, as in 'search by document status'",
              })}
              onSanitize={lifecycleFilterSanitizer(filterableStates)}
              defaultValue={filterableStates}
              options={stateFilterOptions(filterableStates)}
              component={CheckboxGroup}
              workspace={workspace}
            />
            <Filter
              param="date"
              title={message({
                id: 'Date',
                comment: 'Section title. Search filter category',
              })}
              component={DateFilter}
              defaultOption="removed_time"
              optionsCount={1}
              dateFormat={dateFormat}
              workspace={workspace}
            />
            <Filter
              param="tags"
              title={message({
                id: 'Tags',
                comment:
                  "Section title. Search filter category title, as in 'search by document tags'.",
              })}
              defaultValue={[]}
              onSanitize={tagsFilterSanitizer}
              component={TagSelector}
              options={tagsOptions}
              optionsCount={tagsOptions.length}
              setOptions={setTagsOptions}
              workspace={workspace}
              onValueChange={tagsOnValueChange}
            />
          </>
        }
        sorting={
          <Filter
            param="sort"
            defaultValue={defaultSortWithScore([
              '-removed_time',
            ])}
            wrap={false}
            component={AgreementSort}
            onSanitize={sortSanitizer}
            sortables={[
              'created',
              'removed_time',
            ]}
          />
        }
      >
        <TrashedDocumentListWithSearchResult
          hideImport
          sendWorkspaceIds={sendWorkspaceIds}
          showWorkspaceName={showWorkspaceName}
          withoutWorkspaceId={withoutWorkspaceId}
        />
      </SearchWithQueryString>
    </FolderQueryProvider>
  );
};

export const allTrashedDocumentsModule = ({ props: { message } }: MapperProps) => ({
  modules: [
    [],
  ],
  section: message({
    id: 'Search',
    comment: 'Used as the title for the section.',
  }),
  showAsLink: false,
  title: message({
    id: 'Trash',
    comment: 'The page title for the document list when showing all trashed documents',
  }),
});

export default adminPage(allTrashedDocumentsModule)(TrashDocuments);
